export type SpinnerDirection = 'clockwise' | 'counter-clockwise' | 'bounce' | 'even-odd';

const MIN_SEGMENT_OPACITY = 0.18;
const FULL_OPACITY_PROGRESS = 0;
const FADE_END_PROGRESS = 0.7;

function wrapValue(value: number, modulus: number): number {
  if (modulus <= 0) {
    return 0;
  }
  return ((value % modulus) + modulus) % modulus;
}

function wrapProgress(progress: number): number {
  return wrapValue(progress, 1);
}

function normalizeSegmentIndex(segmentCount: number, segmentIndex: number): number {
  const safeSegmentCount = Math.max(1, segmentCount);
  return wrapValue(segmentIndex, safeSegmentCount);
}

export function getSpinnerCycleStepCount(direction: SpinnerDirection, segmentCount: number): number {
  const safeSegmentCount = Math.max(1, segmentCount);
  if (direction === 'bounce') {
    return Math.max(1, (safeSegmentCount - 1) * 2);
  }
  if (direction === 'even-odd') {
    return Math.min(2, safeSegmentCount);
  }
  return safeSegmentCount;
}

function getSpinnerVisitPositions(
  direction: SpinnerDirection,
  segmentCount: number,
  segmentIndex: number,
): number[] {
  const safeSegmentCount = Math.max(1, segmentCount);
  const normalizedSegmentIndex = normalizeSegmentIndex(safeSegmentCount, segmentIndex);

  if (direction === 'counter-clockwise') {
    return [normalizedSegmentIndex];
  }

  if (direction === 'even-odd') {
    return [normalizedSegmentIndex % 2];
  }

  if (direction === 'bounce') {
    if (
      normalizedSegmentIndex === 0
      || normalizedSegmentIndex === safeSegmentCount - 1
      || safeSegmentCount <= 2
    ) {
      return [normalizedSegmentIndex];
    }

    const cycleStepCount = getSpinnerCycleStepCount(direction, safeSegmentCount);
    return [normalizedSegmentIndex, cycleStepCount - normalizedSegmentIndex];
  }

  return [wrapValue(-normalizedSegmentIndex, safeSegmentCount)];
}

export function getSpinnerSegmentVisitProgresses(
  direction: SpinnerDirection,
  segmentCount: number,
  segmentIndex: number,
): number[] {
  const safeSegmentCount = Math.max(1, segmentCount);
  const cycleStepCount = getSpinnerCycleStepCount(direction, safeSegmentCount);
  return getSpinnerVisitPositions(direction, safeSegmentCount, segmentIndex)
    .map((visitPosition) => visitPosition / cycleStepCount);
}

export function spinnerSegmentHasSecondaryVisit(
  direction: SpinnerDirection,
  segmentCount: number,
  segmentIndex: number,
): boolean {
  return getSpinnerSegmentVisitProgresses(direction, segmentCount, segmentIndex).length > 1;
}

export function getSpinnerSegmentAnimationDelay(
  direction: SpinnerDirection,
  segmentCount: number,
  segmentIndex: number,
  visitIndex = 0,
): number {
  const visitProgresses = getSpinnerSegmentVisitProgresses(direction, segmentCount, segmentIndex);
  const safeVisitIndex = Math.min(Math.max(visitIndex, 0), visitProgresses.length - 1);
  const delay = -visitProgresses[safeVisitIndex];
  return Object.is(delay, -0) ? 0 : delay;
}

export function getSpinnerSegmentOpacity(progress: number): number {
  const wrappedProgress = wrapProgress(progress);
  if (wrappedProgress <= FULL_OPACITY_PROGRESS) {
    return 1;
  }
  if (wrappedProgress >= FADE_END_PROGRESS) {
    return MIN_SEGMENT_OPACITY;
  }
  const fadeProgress = wrappedProgress / FADE_END_PROGRESS;
  return 1 - ((1 - MIN_SEGMENT_OPACITY) * fadeProgress);
}

export function getSpinnerSegmentOpacityAtTime(
  direction: SpinnerDirection,
  segmentCount: number,
  segmentIndex: number,
  timeProgress: number,
): number {
  const recentVisitProgress = Math.min(
    ...getSpinnerSegmentVisitProgresses(direction, segmentCount, segmentIndex)
      .map((visitProgress) => wrapProgress(timeProgress + visitProgress)),
  );

  return getSpinnerSegmentOpacity(recentVisitProgress);
}

export function getSpinnerLeadingSegmentIndexAtTime(
  direction: SpinnerDirection,
  segmentCount: number,
  timeProgress: number,
): number {
  const safeSegmentCount = Math.max(1, segmentCount);
  let brightestSegmentIndex = 0;
  let brightestSegmentOpacity = -1;

  for (let segmentIndex = 0; segmentIndex < safeSegmentCount; segmentIndex += 1) {
    const opacity = getSpinnerSegmentOpacityAtTime(
      direction,
      safeSegmentCount,
      segmentIndex,
      timeProgress,
    );
    if (opacity > brightestSegmentOpacity) {
      brightestSegmentOpacity = opacity;
      brightestSegmentIndex = segmentIndex;
    }
  }

  return brightestSegmentIndex;
}

export function getSpinnerLeadingSequence(
  direction: SpinnerDirection,
  segmentCount: number,
): number[] {
  const cycleStepCount = getSpinnerCycleStepCount(direction, segmentCount);
  return Array.from({ length: cycleStepCount }, (_, step) =>
    getSpinnerLeadingSegmentIndexAtTime(direction, segmentCount, step / cycleStepCount),
  );
}
